import { ComponentProps, forwardRef } from 'react';
import { cn } from '../../utils/className';

export const Table = forwardRef<HTMLTableElement, ComponentProps<'table'>>(
  ({ className, ...props }, ref) => (
    <table
      ref={ref}
      className={cn(
        'size-p2 w-full caption-bottom border-spacing-0',
        className
      )}
      {...props}
    />
  )
);
Table.displayName = 'Table';

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  ComponentProps<'thead'>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      `bg-neutral-050 sticky top-0 z-10
      shadow-[0_1px_0_0_var(--color-border-secondary)]`,
      className
    )}
    {...props}
  />
));
TableHeader.displayName = 'TableHeader';

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  ComponentProps<'tbody'>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('', className)} {...props} />
));
TableBody.displayName = 'TableBody';

const TableFooter = forwardRef<
  HTMLTableSectionElement,
  ComponentProps<'tfoot'>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn('sticky bottom-0 z-10', className)}
    {...props}
  />
));
TableFooter.displayName = 'TableFooter';

export const TableRow = forwardRef<HTMLTableRowElement, ComponentProps<'tr'>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        `border-border-secondary data-[state=selected]:bg-surface-focus-support
        border-b bg-white in-[thead]:border-0`,
        className
      )}
      {...props}
    />
  )
);
TableRow.displayName = 'TableRow';

interface TableHeadProps extends ComponentProps<'th'> {
  /** Hide the right border (useful for selection columns) */
  noBorder?: boolean;
}

export const TableHead = forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, noBorder, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        `size-p2 font-regular text-secondary bg-neutral-050 flex h-12
        items-center overflow-hidden px-4 py-3 text-left leading-[1.3]
        whitespace-nowrap first:pl-6`,
        !noBorder &&
          '[box-shadow:inset_-1px_0_0_0_var(--color-border-secondary)]',
        className
      )}
      {...props}
    />
  )
);
TableHead.displayName = 'TableHead';

export const TableCell = forwardRef<HTMLTableCellElement, ComponentProps<'td'>>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={cn(
        `size-p2 text-secondary flex h-12 items-center px-4 py-3 leading-[1.3]
        whitespace-nowrap first:pl-6`,
        className
      )}
      {...props}
    />
  )
);
TableCell.displayName = 'TableCell';

// Export TableWrapper for wrapping tables with optional footers
export { TableWrapper } from './TableWrapper';
// Export TablePagination from the same module
export { TablePagination } from './TablePagination';
